'use client'

import {
  Button,
  type ButtonProps,
  copyTextToClipboard,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@repo/ui'
import { Copy } from '@repo/ui/icons'
import { cloneElement, type ReactElement } from 'react'

import { claimToastId } from '../../contexts/ToastContext/Toast.context'
import { Avatar, type AvatarProps } from './Avatar'

/** Pick the square icon-button size matching a text size's height. */
const toIconSize = (size: ButtonProps['size']): ButtonProps['size'] => {
  switch (size) {
    case 'sm':
    case 'iconSm':
      return 'iconSm'
    case 'md':
    case 'iconMd':
      return 'iconMd'
    case 'lg':
    case 'iconLg':
      return 'iconLg'
    default:
      return 'icon'
  }
}

export interface ClipboardButtonProps {
  toastMessage: string
  toCopy: string
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  duration?: number
  tooltipMessage?: string
  disabled?: boolean
  disableTooltip?: boolean
  label?: string
  avatarProps?: AvatarProps
  children?: ReactElement
  className?: string
}

const ClipboardButton = ({
  toastMessage,
  toCopy,
  duration,
  tooltipMessage,
  disableTooltip = false,
  disabled = false,
  children,
  variant = 'ghost',
  size,
  label,
  avatarProps,
  className
}: ClipboardButtonProps) => {
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    void copyTextToClipboard(toCopy, {
      successMessage: toastMessage,
      toastOptions: () => ({ id: claimToastId('clipboard'), duration })
    })
  }

  if (disabled) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children ? (
          // oxlint-disable-next-line typescript/consistent-type-assertions
          cloneElement(children as ReactElement<Record<string, unknown>>, {
            onClick
          })
        ) : (
          <Button
            variant={variant}
            size={label ? size : toIconSize(size)}
            className={className}
            onClick={onClick}
          >
            {label ? (
              <>
                {avatarProps && <Avatar {...avatarProps} />}
                {label}
                <Copy />
              </>
            ) : (
              <Copy />
            )}
          </Button>
        )}
      </TooltipTrigger>
      {!disableTooltip && <TooltipContent>{tooltipMessage ?? 'Copy to clipboard.'}</TooltipContent>}
    </Tooltip>
  )
}

export { ClipboardButton }
